import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@/lib/ThemeContext";

const STORAGE_KEY = "mmntm:cmdk_hint_shown";
const DISPLAY_MS = 2800;

/**
 * One-shot floating hint that nudges the user to press ⌘K right after
 * the tutorial closes. Renders a small pill in the top bar area with a
 * pulsing accent ring, then fades out — never repeats once the
 * localStorage flag has been set.
 *
 * Trigger: window.dispatchEvent(new CustomEvent("mmntm:show-cmdk-hint"))
 */
export default function CmdKHintPulse() {
  const { tokens: t } = useTheme();
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const fadeTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const onShow = () => {
      try {
        if (localStorage.getItem(STORAGE_KEY) === "1") return;
      } catch {
        // localStorage may be unavailable — fail-open and show once anyway.
      }
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* localStorage unavailable — hint may show again; harmless */ }
      setFading(false);
      setVisible(true);

      // Begin fade just before unmount so the opacity transition is visible.
      fadeTimerRef.current = setTimeout(() => setFading(true), DISPLAY_MS - 500);
      hideTimerRef.current = setTimeout(() => setVisible(false), DISPLAY_MS);
    };
    window.addEventListener("mmntm:show-cmdk-hint", onShow);
    return () => {
      window.removeEventListener("mmntm:show-cmdk-hint", onShow);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (!visible) return null;

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const keyLabel = isMac ? "⌘K" : "Ctrl+K";

  return createPortal(
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[997] pointer-events-none"
      style={{
        opacity: fading ? 0 : 1,
        transition: "opacity 480ms ease-out",
      }}
      data-testid="cmdk-hint-pulse"
      aria-hidden="true"
    >
      <div
        className="mmntm-cmdk-hint relative flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium shadow-lg"
        style={{
          background: t.bgCard || t.bgSecondary,
          border: `1px solid ${t.accent}`,
          color: t.textPrimary,
        }}
      >
        <span style={{ color: t.textSecondary }}>Try it now —</span>
        <kbd
          className="px-1.5 py-0.5 rounded font-mono text-[10px] font-bold"
          style={{
            background: t.bgTertiary,
            border: `1px solid ${t.borderColor}`,
            color: t.accent,
          }}
        >
          {keyLabel}
        </kbd>
      </div>

      {/* Pulsing accent ring (purely decorative) */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
          border: `2px solid ${t.accent}`,
          animation: "mmntm-cmdk-pulse 1400ms ease-out infinite",
          pointerEvents: "none",
        }}
      />

      <style>{`
        @keyframes mmntm-cmdk-pulse {
          0%   { opacity: 0.55; transform: scale(1); }
          70%  { opacity: 0;    transform: scale(1.35); }
          100% { opacity: 0;    transform: scale(1.35); }
        }
      `}</style>
    </div>,
    document.body
  );
}
